import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import usePersistedState from "@/lib/usePersistedState";
import { PageHeader } from "@/components/Layout";
import PeriodPicker, { presetRange } from "@/components/PeriodPicker";
import { inr, formatDate } from "@/lib/utils2";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Trucks() {
  const [trucks, setTrucks] = useState([]);
  const [parties, setParties] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [period, setPeriod] = usePersistedState("period:trucks", () => presetRange("this-month"));
  const [filters, setFilters] = usePersistedState("filters:trucks", {
    party_id: "all", transporter_id: "all", status: "all",
  });

  const load = async () => {
    const params = { date_from: period.from, date_to: period.to };
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "all") params[k] = v;
    });
    const [t, p, tr] = await Promise.all([
      api.get("/trucks", { params }),
      api.get("/parties"),
      api.get("/transporters"),
    ]);
    setTrucks(t.data); setParties(p.data); setTransporters(tr.data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters, period.from, period.to]);

  const partyName = (id) => parties.find((p) => p.id === id)?.name || "—";
  const transporterName = (id) => transporters.find((t) => t.id === id)?.name || "—";

  const remove = async (id) => {
    if (!confirm("Delete this truck entry?")) return;
    try {
      await api.delete(`/trucks/${id}`);
      toast.success("Deleted"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1600px] mx-auto" data-testid="trucks-page">
      <PageHeader
        title="Truck Entries"
        subtitle="Single entry per truck released — Material side + Transport side + GST captured together."
        action={
          <Link to="/trucks/new">
            <Button data-testid="add-truck-btn" className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
              <Plus className="h-4 w-4 mr-1.5" /> New Entry
            </Button>
          </Link>
        }
      />

      <Card className="rounded-sm border-brand-line shadow-none p-5 mb-6">
        <div className="mb-4 pb-4 border-b border-brand-line">
          <PeriodPicker value={period} onChange={(p) => setPeriod({ from: p.from, to: p.to })}
            defaultPreset="this-month" testIdPrefix="trucks-period" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">Party</Label>
            <Select value={filters.party_id} onValueChange={(v) => setFilters({ ...filters, party_id: v })}>
              <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white" data-testid="filter-party"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Parties</SelectItem>
                {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">Transporter</Label>
            <Select value={filters.transporter_id} onValueChange={(v) => setFilters({ ...filters, transporter_id: v })}>
              <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white" data-testid="filter-transporter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {transporters.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unbilled">Has Unbilled Side</SelectItem>
                <SelectItem value="billed">Fully or Partly Billed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-3 py-3">Vehicle</th>
                <th className="text-left px-3 py-3">Party</th>
                <th className="text-left px-3 py-3">Mode</th>
                <th className="text-left px-3 py-3">Transporter</th>
                <th className="text-right px-3 py-3">Mat. Purchase</th>
                <th className="text-right px-3 py-3">Mat. Bill</th>
                <th className="text-right px-3 py-3">Trn. Paid</th>
                <th className="text-right px-3 py-3">Trn. Bill</th>
                <th className="text-right px-3 py-3">Combined Bill</th>
                <th className="text-right px-3 py-3">Profit</th>
                <th className="text-left px-3 py-3">Bill #</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {trucks.length === 0 && (
                <tr><td colSpan={13} className="px-4 py-10 text-center text-brand-muted text-sm">No truck entries match the current filters.</td></tr>
              )}
              {trucks.map((t) => (
                <tr key={t.id} className="border-t border-brand-line hover:bg-secondary/40" data-testid={`truck-row-${t.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(t.entry_date)}</td>
                  <td className="px-3 py-3 font-medium">{t.vehicle_number}</td>
                  <td className="px-3 py-3 truncate max-w-[160px]" title={partyName(t.party_id)}>{partyName(t.party_id)}</td>
                  <td className="px-3 py-3 text-xs">
                    <div className="flex flex-col gap-1">
                      <Badge
                        className={`rounded-sm text-[9px] tracking-wider w-fit ${
                          t.purchase_mode === "combined"
                            ? "bg-brand-terracotta/15 text-brand-terracotta border border-brand-terracotta/40"
                            : "bg-secondary text-brand-muted border border-brand-line"
                        }`}
                        data-testid={`purchase-mode-badge-${t.id}`}
                      >
                        P · {t.purchase_mode === "combined" ? "COMB" : "SPLIT"}
                      </Badge>
                      <Badge
                        className={`rounded-sm text-[9px] tracking-wider w-fit ${
                          t.billing_mode === "combined"
                            ? "bg-brand-sand text-brand-ink border border-brand-sand"
                            : "bg-secondary text-brand-muted border border-brand-line"
                        }`}
                        data-testid={`sale-mode-badge-${t.id}`}
                      >
                        S · {t.billing_mode === "combined" ? "COMB" : "SPLIT"}
                      </Badge>
                    </div>
                  </td>
                  <td className="px-3 py-3 truncate max-w-[140px] text-brand-muted" title={transporterName(t.transporter_id)}>{transporterName(t.transporter_id)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-brand-muted">{inr(t.material_purchase_amount)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{t.billing_mode === "combined" ? <span className="text-brand-muted">—</span> : inr(t.material_bill_amount)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-brand-muted">{inr(t.transport_purchase_amount)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{t.billing_mode === "combined" ? <span className="text-brand-muted">—</span> : inr(t.transport_bill_amount)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{t.billing_mode === "combined" ? inr(t.combined_bill_amount) : <span className="text-brand-muted">—</span>}</td>
                  <td className="px-3 py-3 text-right">
                    <Badge
                      className={`rounded-sm tabular-nums font-medium ${
                        (t.profit || 0) > 0
                          ? "bg-brand-forest/10 text-brand-forest border border-brand-forest/30"
                          : (t.profit || 0) < 0
                          ? "bg-brand-terracotta/10 text-brand-terracotta border border-brand-terracotta/30"
                          : "bg-secondary text-brand-muted border border-brand-line"
                      }`}
                      data-testid={`profit-chip-${t.id}`}
                    >
                      {inr(t.profit)}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 space-y-0.5">
                    {t.billing_mode === "combined" ? (
                      t.combined_bill_id ? (
                        <Link to={`/bills/${t.combined_bill_id}`}>
                          <Badge className="bg-brand-forest text-white rounded-sm">{t.combined_bill_number}</Badge>
                        </Link>
                      ) : <Badge variant="outline" className="rounded-sm border-brand-sand text-brand-sand">Pending</Badge>
                    ) : (
                      <>
                        {t.material_bill_id ? (
                          <Link to={`/bills/${t.material_bill_id}`}>
                            <Badge className="bg-brand-forest text-white rounded-sm text-[10px]">M: {t.material_bill_number}</Badge>
                          </Link>
                        ) : t.material_bill_rate > 0 ? (
                          <Badge variant="outline" className="rounded-sm border-brand-sand text-brand-sand text-[10px]">M pending</Badge>
                        ) : null}
                        {t.transport_bill_id ? (
                          <Link to={`/bills/${t.transport_bill_id}`} className="block">
                            <Badge className="bg-brand-forest text-white rounded-sm text-[10px]">T: {t.transport_bill_number}</Badge>
                          </Link>
                        ) : t.transport_bill_rate > 0 ? (
                          <Badge variant="outline" className="rounded-sm border-brand-sand text-brand-sand text-[10px]">T pending</Badge>
                        ) : null}
                        {!t.material_bill_rate && !t.transport_bill_rate && <span className="text-xs text-brand-muted">—</span>}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 justify-end">
                      <Link to={`/trucks/${t.id}/edit`}>
                        <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`edit-truck-${t.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </Link>
                      {!t.material_bill_id && !t.transport_bill_id && !t.combined_bill_id && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-brand-terracotta"
                          onClick={() => remove(t.id)} data-testid={`delete-truck-${t.id}`}
                          data-admin-only="true">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
